import React, { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, Search, LogOut, Menu, X, Disc3, MessagesSquare, CalendarCheck, User as UserIcon } from "lucide-react";
import { api, resolveFileUrl } from "../api";
import { useAuth, useCart } from "../contexts";

const links = [
  { to: "/mixtapes", label: "Mixtapes" },
  { to: "/singles", label: "Singles" },
  { to: "/videos", label: "Videos" },
  { to: "/books", label: "Books" },
  { to: "/merch", label: "Merch" },
  { to: "/culture-connect", label: "Culture Connect" },
  { to: "/jobs", label: "Jobs" },
  { to: "/culture-pass", label: "Culture Pass" },
];

export default function Navigation() {
  const { user, logout, culturePassActive } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    let alive = true;
    if (!user) {
      queueMicrotask(() => alive && setUnread(0));
      return () => { alive = false; };
    }
    const fetchUnread = async () => {
      try {
        const r = await api.get("/messages/unread-count");
        if (alive) setUnread(r.data?.unread || 0);
      } catch (err) {
        // Non-blocking — unread badge is best-effort
        if (process.env.NODE_ENV !== "production") {
          console.warn("[nav] unread-count fetch failed:", err?.response?.status || err?.message);
        }
      }
    };
    fetchUnread();
    const id = setInterval(fetchUnread, 30000);
    return () => { alive = false; clearInterval(id); };
  }, [user]);

  const linkClass = ({ isActive }) =>
    `font-mono-label text-[11px] transition-colors ${
      isActive ? "text-[var(--accent)]" : "text-neutral-300 hover:text-white"
    }`;

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 backdrop-blur-xl border-b border-neutral-800"
      style={{ background: "rgba(5,5,5,0.6)" }}
    >
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 shrink-0">
          <Disc3 size={22} className="text-[var(--accent)]" />
          <span className="font-display text-2xl uppercase leading-none">
            Hip<span className="text-[var(--accent)]">Hop</span>IsTheCulture
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} className={linkClass} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
              {l.label.toUpperCase()}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/search")}
            className="hidden sm:inline-flex text-neutral-300 hover:text-white"
            data-testid="search-btn"
            aria-label="Search"
          >
            <Search size={18} />
          </button>
          {user && (
            <>
              <Link to="/bookings" className="hidden sm:inline-flex text-neutral-300 hover:text-white" data-testid="nav-bookings" title="Bookings">
                <CalendarCheck size={18} />
              </Link>
              <Link to="/messages" className="hidden sm:inline-flex text-neutral-300 hover:text-white relative" data-testid="nav-messages" title="Messages">
                <MessagesSquare size={18} />
                {unread > 0 && (
                  <span className="absolute -top-2 -right-2 bg-[var(--accent)] text-black text-[10px] font-mono-label px-1.5" data-testid="nav-unread">
                    {unread}
                  </span>
                )}
              </Link>
            </>
          )}
          <Link
            to="/cart"
            className="relative text-neutral-200 hover:text-[var(--accent)] transition-colors"
            data-testid="cart-btn"
            aria-label="Cart"
          >
            <ShoppingBag size={20} />
            {count > 0 && (
              <span
                className="absolute -top-2 -right-2 bg-[var(--accent)] text-black text-[10px] font-mono-label px-1.5"
                data-testid="cart-count"
              >
                {count}
              </span>
            )}
          </Link>

          {user ? (
            <div className="hidden md:flex items-center gap-3">
              {culturePassActive && (
                <Link to="/culture-pass" className="chip chip-accent" data-testid="nav-pass-badge" title="Culture Pass active">
                  MEMBER
                </Link>
              )}
              {(user.is_artist || user.role === "admin") && (
                <Link to="/studio" className="font-mono-label text-[11px] text-neutral-300 hover:text-[var(--accent)]" data-testid="nav-studio" title="Artist Studio">
                  STUDIO
                </Link>
              )}
              {user.role === "admin" && (
                <Link to="/admin" className="chip chip-accent" data-testid="admin-link">
                  Admin
                </Link>
              )}
              <Link to={`/artist/${user.username}`} className="font-mono-label text-[11px] text-neutral-300 hover:text-white" data-testid="profile-link">
                {user.username}
              </Link>
              <button onClick={logout} className="text-neutral-400 hover:text-white" data-testid="logout-btn" aria-label="Logout">
                <LogOut size={16} />
              </button>
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-2">
              <Link to="/login" className="btn-ghost !py-2 !px-3" data-testid="login-link">
                Sign In
              </Link>
              <Link to="/register" className="btn-primary !py-2 !px-3" data-testid="register-link">
                Join
              </Link>
            </div>
          )}

          <button
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden text-white"
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-neutral-800 bg-black/95 px-4 py-4 flex flex-col gap-4" data-testid="mobile-nav">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              className="font-mono-label text-sm"
              data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {l.label.toUpperCase()}
            </NavLink>
          ))}
          <div className="divider-tape" />
          {user ? (
            <>
              {/* User profile header */}
              <Link
                onClick={() => setOpen(false)}
                to={`/profile/${user.username}`}
                className="flex items-center gap-3 p-3 -mx-1 border border-neutral-800 hover:border-[var(--accent)] transition-colors"
                data-testid="mobile-nav-profile"
              >
                {user.avatar ? (
                  <img src={resolveFileUrl(user.avatar)} alt="" className="w-11 h-11 object-cover border border-neutral-800" />
                ) : (
                  <div className="w-11 h-11 border border-neutral-800 bg-neutral-900 flex items-center justify-center">
                    <UserIcon size={18} className="text-[var(--accent)]" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-headline text-base uppercase truncate">{user.display_name || user.username}</div>
                  <div className="font-mono-label text-[10px] text-neutral-500 truncate">VIEW MY PROFILE · @{user.username}</div>
                </div>
              </Link>
              {user.role === "admin" && (
                <Link onClick={() => setOpen(false)} to="/admin" className="font-mono-label text-sm text-[var(--accent)]" data-testid="mobile-nav-admin">
                  Admin Dashboard
                </Link>
              )}
              {(user.is_artist || user.role === "admin") && (
                <Link onClick={() => setOpen(false)} to="/studio" className="font-mono-label text-sm" data-testid="mobile-nav-studio">
                  Studio
                </Link>
              )}
              <Link onClick={() => setOpen(false)} to="/bookings" className="font-mono-label text-sm" data-testid="mobile-nav-bookings">
                Bookings
              </Link>
              <Link onClick={() => setOpen(false)} to="/messages" className="font-mono-label text-sm" data-testid="mobile-nav-messages">
                Messages {unread > 0 && <span className="text-[var(--accent)]">· {unread}</span>}
              </Link>
              <Link onClick={() => setOpen(false)} to="/bookmarks" className="font-mono-label text-sm" data-testid="mobile-nav-bookmarks">
                Bookmarks
              </Link>
              <Link onClick={() => setOpen(false)} to="/orders" className="font-mono-label text-sm" data-testid="mobile-nav-orders">
                My Orders
              </Link>
              <button onClick={() => { logout(); setOpen(false); }} className="font-mono-label text-sm text-left" data-testid="mobile-nav-logout">
                Sign Out
              </button>
            </>
          ) : (
            <div className="flex gap-2">
              <Link onClick={() => setOpen(false)} to="/login" className="btn-ghost !py-2">Sign In</Link>
              <Link onClick={() => setOpen(false)} to="/register" className="btn-primary !py-2">Join</Link>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
